import { isEmpty, uniq } from "lodash";
import maxBy from "lodash/maxBy";
import { createSelector } from "reselect";

import {
  BillingDocumentIntegrationStatus,
  BillingDocumentType,
} from "constants/billingDocuments";
import { IntegrationTypes } from "constants/integrations";

import { EMPTY_ARRAY } from "lib";

import {
  billingDocumentIsApproved,
  billingDocumentIsPaid,
} from "lib/billingDocument";
import { calculateInterestChargeCentsAndDaysToCharge } from "lib/interest";
import { dateTimeStringToDateTime, getDaysDifferent } from "lib/timeFormats";

import { getIsLivestockAgent } from "../auth";
import {
  selectBusinessForBillingDocumentByIdLookup,
  selectWarningsByBillingDocumentIdLookup,
} from "../billingDocuments";
import { getDefaultBillingSettings } from "../billingSettings";
import { getActiveLivestockAgentDeployment } from "../deploymentActiveAgent";
import { selectIntegrationBusinessesByBusinessIdByTypeLookup } from "../integrationBusinesses";
import { selectActiveInterestRateSettingsByBusinessIdLookup } from "../interest";
import { selectPaymentsByBillingDocumentIdLookup } from "../payments";
import {
  getBillingDocuments,
  getBillingRuns,
  getConsignments,
  getEmails,
  getSales,
  getSaleSubTypes,
} from "../root";
import {
  selectConsignmentIdsByDeploymentSaleIdLookup,
  selectConsignmentIdsByVendorIdLookup,
} from "../indexes";
import { getCurrentDeploymentSalesList } from "../currentSale";
import {
  selectDefaultVendorCommissionByDeploymentSaleId,
  selectVendorCommissionBandsByDeploymentBusinessIdLookup,
} from "../vendorCommissionBands";
import { getActiveBillingRunId } from "../billingRuns";
import { selectEffectiveSaleTitleBySaleIdLookup } from "../sales";

const getDeploymentSaleCommissionForVendor = (
  deploymentSaleCommissionValue,
  sale,
  vendorCommissionBands,
) => {
  const commission = {
    deploymentSaleCommissionValue,
    customCommissionValue: null,
  };

  vendorCommissionBands.forEach(band => {
    const differsFromDefault =
      band.commissionValue !== commission.deploymentSaleCommissionValue;
    if (differsFromDefault) {
      if (band.saleSubTypeId === sale.sale_sub_type_id) {
        commission.customCommissionValue = band.commissionValue;
      }
      if (
        isEmpty(commission.customCommissionValue) &&
        band.saleSubTypeId === null
      ) {
        commission.customCommissionValue = band.commissionValue;
      }
    }
  });
  return commission;
};

function showOnOutstandingSettlementReport(
  billingDocument,
  business,
  payments,
) {
  // Only type invoice.
  if (billingDocument.type !== BillingDocumentType.INVOICE) {
    return false;
  }
  // Filtered for Businesses that are included in the DINS grouping
  if (!business.hasDebtorInsurance) {
    return false;
  }

  // Exclude voided or deleted invoices
  if (
    [
      BillingDocumentIntegrationStatus.VOIDED,
      BillingDocumentIntegrationStatus.DELETED,
    ].includes(billingDocument.integrationStatus)
  ) {
    return false;
  }

  // We want to show invoices that are either:
  // - Paid in the last 7 days
  // - Approved and unpaid
  const mostRecentPayment = maxBy(payments, "paymentDate");
  const paidDateStr = mostRecentPayment?.paymentDate;

  // Show invoices paid in the last 7 days
  if (paidDateStr && billingDocumentIsPaid(billingDocument, payments)) {
    const paidDate = dateTimeStringToDateTime(paidDateStr);
    const daysSincePaid = getDaysDifferent(new Date(), paidDate);
    if (daysSincePaid <= 7) {
      return true;
    }
  }

  // Show approved and unpaid invoices
  if (
    billingDocumentIsApproved(billingDocument) &&
    !billingDocumentIsPaid(billingDocument, payments)
  ) {
    return true;
  }

  return false;
}

const transformBillingDocumentDataForAggrid = (
  billingDocument,
  emailByIdLookup,
  warningsByBillingDocumentIdLookup,
  saleByIdLookup,
  billingRunByIdLookup,
  defaultVendorCommissionByDeploymentSaleIdLookup,
  vendorCommissionBandsByDeploymentBusinessIdLookup,
  businessForBillingDocumentByIdLookup,
  integrationBusinessesByTypeByBusinessIdLookup,
  activeInterestRateSettingsByBusinessIdLookup,
  billingSettings,
  saleSubTypeByIdLookup,
  paymentsByBillingDocumentIdLookup,
  activeLivestockAgentDeployment,
  isLivestockAgent,
  vendorNumberByBillingDocumentIdLookup,
  effectiveSaleTitleBySaleIdLookup,
) => {
  const business = businessForBillingDocumentByIdLookup[billingDocument.id];
  const xeroIntegrationBusiness =
    integrationBusinessesByTypeByBusinessIdLookup[IntegrationTypes.Xero]?.[
      business.id
    ]?.[0] || null;
  const interestSettings =
    activeInterestRateSettingsByBusinessIdLookup[business.id]?.[0] || {};

  const payments =
    paymentsByBillingDocumentIdLookup[billingDocument.id] || EMPTY_ARRAY;

  // Billing documents at the moment generally only have one related livestock sale, but if they have more, we only need to check the first one
  const livestockSale = saleByIdLookup[billingDocument.livestockSaleIds?.[0]];
  const saleSubType = saleSubTypeByIdLookup[livestockSale?.sale_sub_type_id];
  const calculatedInterest = calculateInterestChargeCentsAndDaysToCharge(
    billingDocument,
    interestSettings,
    null,
    new Date(),
    billingSettings || {},
    saleSubType,
    payments,
  ).interestToChargeCents;
  const warnings = warningsByBillingDocumentIdLookup[billingDocument.id];

  // Find the vendor commission band for this business and the default vendor commission band (for this sale sub type).
  let commission = {
    deploymentSaleCommissionValue: null,
    customCommissionValue: null,
  };

  if (
    isLivestockAgent &&
    activeLivestockAgentDeployment &&
    activeLivestockAgentDeployment.id &&
    livestockSale &&
    billingDocument.livestockSaleIds?.length === 1
  ) {
    const billingRun = billingRunByIdLookup[billingDocument.billingRunId];

    // Only dig in the deployment commissions if we have an active livestock agent deployment
    // This maybe an AG-5236 (Saleyard billing) todo
    if (livestockSale && billingRun) {
      const deploymentSaleId = livestockSale.deployment_sales.find(
        deploymentSale =>
          deploymentSale.deployment_id === activeLivestockAgentDeployment.id,
      ).deployment_sale_id;
      // We are in the context of a single sale, so determine the commission information for this vendor in this sale.
      // We use this information to determine whether the commission value is the default or custom value.
      commission = getDeploymentSaleCommissionForVendor(
        defaultVendorCommissionByDeploymentSaleIdLookup[deploymentSaleId],
        livestockSale,
        vendorCommissionBandsByDeploymentBusinessIdLookup[
          business.deploymentBusinessId
        ] || [],
      );
    }
  }

  billingDocument.warnings = warnings;
  const livestockSales = billingDocument.livestockSaleIds.map(
    livestockSaleId => {
      const sale = saleByIdLookup[livestockSaleId];
      const saleTitle = effectiveSaleTitleBySaleIdLookup[livestockSaleId];
      return { ...sale, sale_title: saleTitle };
    },
  );

  const collatedInterestSettings = Object.assign({}, interestSettings, {
    calculatedInterest,
  });

  return {
    billingDocument,
    business: Object.assign({}, business, {
      xeroIntegrationBusiness,
    }),
    commission,
    emails:
      billingDocument.emailIds?.map(id => emailByIdLookup[id]) || EMPTY_ARRAY,
    interestSettings: collatedInterestSettings,
    livestockSales,
    payments,
    showOnOutstandingSettlementReport: showOnOutstandingSettlementReport(
      billingDocument,
      business,
      payments,
    ),
    vendorNumber:
      vendorNumberByBillingDocumentIdLookup[billingDocument.id] ?? null,
    warnings,
  };
};

/**
 * Vendor number for each billing document, from consignments in the current
 * sale. RCTI: recipient; Invoice: issuer (normally none, as issuer is the buyer).
 */
export const selectVendorNumberByBillingDocumentIdLookup = createSelector(
  [
    getBillingDocuments,
    getConsignments,
    getCurrentDeploymentSalesList,
    selectConsignmentIdsByDeploymentSaleIdLookup,
    selectConsignmentIdsByVendorIdLookup,
  ],
  (
    billingDocumentByIdLookup,
    consignmentByIdLookup,
    deploymentSalesList,
    consignmentIdsByDeploymentSaleId,
    consignmentIdsByVendorId,
  ) => {
    const currentSaleConsignmentIds = new Set(
      deploymentSalesList?.flatMap(
        ds => consignmentIdsByDeploymentSaleId[ds.deployment_sale_id] || [],
      ),
    );
    return Object.fromEntries(
      Object.values(billingDocumentByIdLookup).map(doc => {
        const businessId =
          doc.type === BillingDocumentType.RCTI
            ? doc.recipientBusinessId
            : doc.issuerBusinessId;
        const idsInCurrentSale = (
          consignmentIdsByVendorId[businessId] || []
        ).filter(id => currentSaleConsignmentIds.has(id));
        const vendorNumbers = uniq(
          idsInCurrentSale
            .map(id => {
              const consignment = consignmentByIdLookup[id];
              const prefixCode = consignment?.livestock_agency_code || "";
              const vendorNumber = consignment?.vendorNumber || "";
              return `${prefixCode}${vendorNumber}`;
            })
            .filter(Boolean),
        );
        return [
          doc.id,
          vendorNumbers.length > 0 ? vendorNumbers.join(", ") : null,
        ];
      }),
    );
  },
);

export const selectBillingDocumentsAggridDataById = createSelector(
  [
    getBillingDocuments,
    getEmails,
    selectWarningsByBillingDocumentIdLookup,
    getSales,
    getBillingRuns,
    selectDefaultVendorCommissionByDeploymentSaleId,
    selectVendorCommissionBandsByDeploymentBusinessIdLookup,
    selectBusinessForBillingDocumentByIdLookup,
    selectIntegrationBusinessesByBusinessIdByTypeLookup,
    selectActiveInterestRateSettingsByBusinessIdLookup,
    getDefaultBillingSettings,
    getSaleSubTypes,
    selectPaymentsByBillingDocumentIdLookup,
    getActiveLivestockAgentDeployment,
    getIsLivestockAgent,
    selectVendorNumberByBillingDocumentIdLookup,
    selectEffectiveSaleTitleBySaleIdLookup,
  ],
  (
    billingDocumentByIdLookup,
    emailByIdLookup,
    warningsByBillingDocumentIdLookup,
    saleByIdLookup,
    billingRunByIdLookup,
    defaultVendorCommissionByDeploymentSaleIdLookup,
    vendorCommissionBandsByDeploymentBusinessIdLookup,
    businessForBillingDocumentByIdLookup,
    integrationBusinessesByTypeByBusinessIdLookup,
    activeInterestRateSettingsByBusinessIdLookup,
    billingSettings,
    saleSubTypeByIdLookup,
    paymentsByBillingDocumentIdLookup,
    activeLivestockAgentDeployment,
    isLivestockAgent,
    vendorNumberByBillingDocumentIdLookup,
    effectiveSaleTitleBySaleIdLookup,
  ) =>
    Object.values(billingDocumentByIdLookup).reduce((acc, billingDocument) => {
      acc[billingDocument.id] = transformBillingDocumentDataForAggrid(
        billingDocument,
        emailByIdLookup,
        warningsByBillingDocumentIdLookup,
        saleByIdLookup,
        billingRunByIdLookup,
        defaultVendorCommissionByDeploymentSaleIdLookup,
        vendorCommissionBandsByDeploymentBusinessIdLookup,
        businessForBillingDocumentByIdLookup,
        integrationBusinessesByTypeByBusinessIdLookup,
        activeInterestRateSettingsByBusinessIdLookup,
        billingSettings,
        saleSubTypeByIdLookup,
        paymentsByBillingDocumentIdLookup,
        activeLivestockAgentDeployment,
        isLivestockAgent,
        vendorNumberByBillingDocumentIdLookup,
        effectiveSaleTitleBySaleIdLookup,
      );
      return acc;
    }, {}),
);

export const getBillingDocumentsAggridDataById = billingDocumentId => state =>
  selectBillingDocumentsAggridDataById(state)[billingDocumentId];

export const selectBillingDocumentsAggridData = createSelector(
  [selectBillingDocumentsAggridDataById],
  billingDocumentsAggridDataById =>
    Object.values(billingDocumentsAggridDataById),
);

export const selectActiveBillingRunBillingDocumentsAggridData = createSelector(
  [selectBillingDocumentsAggridData, getActiveBillingRunId],
  (billingDocumentsAggriddata, activeBillingRunId) =>
    billingDocumentsAggriddata.filter(
      billingDocumentRow =>
        billingDocumentRow.billingDocument.billingRunId === activeBillingRunId,
    ),
);

export const selectActiveBillingRunWarnings = createSelector(
  [selectActiveBillingRunBillingDocumentsAggridData],
  billingDocumentsAggridData =>
    billingDocumentsAggridData.filter(doc => doc.warnings.length > 0),
);

export const selectEmailRecipientsByActiveBillingDocumentId = createSelector(
  [selectActiveBillingRunBillingDocumentsAggridData],
  billingDocumentRowData =>
    billingDocumentRowData.reduce((acc, { billingDocument, business }) => {
      acc[billingDocument.id] =
        business.emailRecipients?.filter(er => er.email) || EMPTY_ARRAY;
      return acc;
    }, {}),
);
